import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import {
	Navbar,
	NavbarLeft,
	NavbarCenter,
	NavbarRight,
	NavbarButton,
	NavbarTitle,
} from 'components/Widget/Navbar';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { MenuItemAction } from 'menu/MenuItem';
import * as S from './styles';
import { RootReducer } from 'redux/reducers';
import {
	selectActiveDesignSystem,
	selectActiveDesignSystemVersion,
	selectActiveDesignSystemIsPreparing,
} from 'redux/db/reducers';
import { selectDesignSystemChatPanelWidth } from 'redux/mode/reducers';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { showConfirmModal } from 'redux/mode/actions';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { Loader } from 'components/Widget/Loader';
import { Tooltip } from 'components/Widget/Tooltip';
import ShareDesignSystemModal from 'components/Modal/Share/ShareDesignSystemModal';
import { trackDesignSystemAction } from 'analytics';
import JSZip from 'jszip';
import { downloadBlob } from 'utils';
import { DesignSystemSection } from 'designSystem/types';
import { VITE_FORGE_URL } from '@constants';
import TabBar from './TabBar';
import PreviewBgPicker from './PreviewBgPicker';
import Search from './Search';

interface TopBarProps {
	onAddTab: (section: DesignSystemSection, contentPath?: string, label?: string) => void;
}

// Width thresholds based on window.innerWidth
const WIDTH_SEARCH_ICON = 1000;
const WIDTH_SHARE_ICON = 900;
const WIDTH_SEARCH_HIDDEN = 700;
const WIDTH_DOWNLOAD_HIDDEN = 600;

const TopBar = ({ onAddTab }: TopBarProps) => {
	const dispatch = useDispatch();
	const {
		activeDesignSystem,
		activeDesignSystemVersion,
		latestPublishedVersionNumber,
		designSystemContent,
		chatPanelWidth,
		isPreparing,
	} = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			latestPublishedVersionNumber: r.db.latestPublishedVersionNumber,
			designSystemContent: r.mode.designSystemContent,
			chatPanelWidth: selectDesignSystemChatPanelWidth(r.mode),
			isPreparing: selectActiveDesignSystemIsPreparing(r.db),
		}),
		shallowEqual
	);
	const [spinnerTooltipId] = useState(String(Math.random()));
	const [errorTooltipId] = useState(String(Math.random()));
	const [showShareModal, setShowShareModal] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [windowWidth, setWindowWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 1200
	);

	useEffect(() => {
		const onResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onDiscard = () => {
		if (!activeDesignSystem?.id) return;

		dispatch(
			showConfirmModal({
				title: 'Discard Changes?',
				description:
					'Are you sure you want to discard all draft changes? This action cannot be undone.',
				cancelLabel: 'Cancel',
				submitLabel: 'Discard Changes',
				onSubmit: async () => {
					try {
						await forge.discardDsVersion(activeDesignSystem.id);
						toast.success('Draft changes discarded');
					} catch (error) {
						toast.error('Failed to discard changes');
					}
				},
			})
		);
	};

	const onPublish = () => {
		if (!activeDesignSystem?.id) return;

		dispatch(
			showConfirmModal({
				title: 'Publish Design System?',
				description:
					'Are you sure you want to publish? This draft will become the latest published version.',
				cancelLabel: 'Cancel',
				submitLabel: 'Publish',
				onSubmit: async () => {
					try {
						const response = await forge.publishDsVersion(activeDesignSystem.id);
						toast.success(`Design system published as v${response.versionNumber}`);
					} catch (error) {
						toast.error('Failed to publish design system');
					}
				},
			})
		);
	};

	const onDownloadDs = async () => {
		// designSystemContent.files comes from the active version (draft if exists, otherwise latest published)
		// This matches what the user sees on screen, so the download will match the current view
		const files = designSystemContent?.files;
		if (!files || files.length === 0) {
			toast.error('No design system content available to download');
			return;
		}

		setIsDownloading(true);

		try {
			const zip = new JSZip();

			// Add text files (non-assets) - content is stored directly
			for (const file of files) {
				if (file.path.startsWith('assets/')) continue; // Handle separately
				zip.file(file.path, file.content);
			}

			// Fetch and add asset files
			// Assets have their content field set to a CAS storage path, not the actual content.
			// We use ?proxy=true to get the content directly (avoids CORS issues with signed URL redirects).
			const assetFiles = files.filter((f) => f.path.startsWith('assets/'));
			if (assetFiles.length > 0) {
				const failedAssets: string[] = [];

				const assetPromises = assetFiles.map(async (file) => {
					try {
						// file.content is the CAS storage path for assets
						const storagePath = encodeURIComponent(file.content);
						const response = await fetch(`${VITE_FORGE_URL}/attachment/${storagePath}?proxy=true`, {
							credentials: 'include',
						});

						if (!response.ok) {
							failedAssets.push(file.path);
							return null;
						}

						const blob = await response.blob();
						return { path: file.path, blob };
					} catch {
						failedAssets.push(file.path);
						return null;
					}
				});

				const assetResults = await Promise.all(assetPromises);

				for (const result of assetResults) {
					if (result) {
						zip.file(result.path, result.blob);
					}
				}

				if (failedAssets.length > 0) {
					Sentry.captureMessage(
						`DS ZIP download missing assets: ${failedAssets.join(', ')}`,
						'warning'
					);
				}
			}

			const zipBlob = await zip.generateAsync({ type: 'blob' });
			const trimmedTitle = activeDesignSystem?.title?.trim();
			const filename = trimmedTitle
				? `${trimmedTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-ds.zip`
				: 'design-system.zip';

			// Reset loading state before showing file dialog (dialog is the confirmation)
			setIsDownloading(false);

			downloadBlob(zipBlob, filename);
		} catch (error) {
			toast.error('Failed to create download file');
			setIsDownloading(false);
		}
	};

	const versionNumber = activeDesignSystemVersion?.versionNumber ?? null;
	const isDraft = versionNumber === 0 && latestPublishedVersionNumber !== null;
	const isUnpublished = versionNumber === 0 && latestPublishedVersionNumber === null;
	const promptGenerationStatus = activeDesignSystemVersion?.manifest?.promptGenerationStatus;
	const showSpinner =
		promptGenerationStatus === 'pending' || promptGenerationStatus === 'generating';
	const showError = promptGenerationStatus === 'failed';

	const spinnerTooltipMessage = 'Preparing DS for use';
	const errorTooltipMessage = 'Preparing DS for use failed';

	const searchMode =
		windowWidth < WIDTH_SEARCH_HIDDEN
			? 'hidden'
			: windowWidth < WIDTH_SEARCH_ICON
			? 'iconOnly'
			: 'full';
	const showShareText = windowWidth >= WIDTH_SHARE_ICON;
	const showDownload = windowWidth >= WIDTH_DOWNLOAD_HIDDEN;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const renderVersionDropdown = () => {
		if (isDraft) {
			return (
				<DropdownMenu
					menuItems={[
						new MenuItemAction('Publish', '', onPublish),
						new MenuItemAction('Discard Changes', '', onDiscard),
					]}
				>
					<S.VersionDropdown>
						{`v${latestPublishedVersionNumber + 1} (draft)`}
						{(showSpinner || showError) && (
							<S.StatusIndicator>
								{showSpinner && (
									<>
										<span
											data-tooltip-id={spinnerTooltipId}
											data-tooltip-content={spinnerTooltipMessage}
										>
											<Loader width={14} height={14} />
										</span>
										<Tooltip id={spinnerTooltipId} placement="bottom" />
									</>
								)}
								{showError && (
									<>
										<span
											data-tooltip-id={errorTooltipId}
											data-tooltip-content={errorTooltipMessage}
										>
											<Icon type={IconType.ERROR} width={14} height={14} />
										</span>
										<Tooltip id={errorTooltipId} placement="bottom" />
									</>
								)}
							</S.StatusIndicator>
						)}
						<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} />
					</S.VersionDropdown>
				</DropdownMenu>
			);
		}

		return (
			<S.VersionText>
				{isUnpublished ? 'Unpublished' : `v${versionNumber ?? '?'}`}
				{(showSpinner || showError) && (
					<S.StatusIndicator>
						{showSpinner && (
							<>
								<span
									data-tooltip-id={spinnerTooltipId}
									data-tooltip-content={spinnerTooltipMessage}
								>
									<Loader width={14} height={14} />
								</span>
								<Tooltip id={spinnerTooltipId} placement="bottom" />
							</>
						)}
						{showError && (
							<>
								<span data-tooltip-id={errorTooltipId} data-tooltip-content={errorTooltipMessage}>
									<Icon type={IconType.ERROR} width={14} height={14} />
								</span>
								<Tooltip id={errorTooltipId} placement="bottom" />
							</>
						)}
					</S.StatusIndicator>
				)}
			</S.VersionText>
		);
	};

	return (
		<>
			<Navbar layout="left-aligned">
				<NavbarLeft width={chatPanelWidth}>
					<NavbarTitle>{activeDesignSystem?.title || 'Untitled Design System'}</NavbarTitle>
					{renderVersionDropdown()}
				</NavbarLeft>
				{isPreparing && (
					<NavbarCenter align="left">
						{/* for now hack together a little placeholder to make this fill height and match the look when not preparing */}
						<div style={{ height: '48px', width: '100%' }}></div>
					</NavbarCenter>
				)}

				{!isPreparing && (
					<NavbarCenter align="left">
						<TabBar onAddTab={onAddTab} />
					</NavbarCenter>
				)}

				{!isPreparing && (
					<NavbarRight>
						<Search
							onAddTab={onAddTab}
							designSystemContent={designSystemContent}
							compactMode={searchMode}
						/>
						<PreviewBgPicker />
						{showDownload && (
							<S.Download>
								<NavbarButton
									icon={isDownloading ? undefined : IconType.LINE_ARROW_DOWN}
									onClick={onDownloadDs}
									tooltip={
										isDownloading
											? 'Preparing download... Fetching files and assets'
											: 'Download DS'
									}
									tooltipId="download-kit-tooltip"
									disabled={isDownloading}
								>
									{isDownloading && <Loader width={16} height={16} />}
								</NavbarButton>
							</S.Download>
						)}
						<S.Share>
							{showShareText ? (
								<Button
									onSelected={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									type={ButtonType.NOTICEABLE}
								>
									Share
								</Button>
							) : (
								<NavbarButton
									icon={IconType.SHARE}
									onClick={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									tooltip="Share"
									tooltipId="share-design-system-tooltip"
								/>
							)}
						</S.Share>
					</NavbarRight>
				)}
			</Navbar>
			{showShareModal && activeDesignSystem && (
				<ShareDesignSystemModal
					designSystem={activeDesignSystem}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
